import {
  Bug,
  Check,
  Coins,
  EyeOff,
  FileLock2,
  Gauge,
  GitMerge,
  GitPullRequest,
  KeyRound,
  ListChecks,
  Lock,
  Menu,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TestTube2,
  TimerReset,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonClasses } from '../components/ui/Button';
import { ChalkUnderline } from '../components/ui/Chalk';
import { useMe, usePublicInfo } from '../hooks/useAuth';
import { Logo } from '../layouts/AppLayout';
import type { Chalk } from '../lib/chalk';
import { CHALK } from '../lib/chalk';
import { cn } from '../lib/cn';

const SECTIONS = [
  { id: 'how-it-works', label: 'How it works' },
  { id: 'what-it-checks', label: 'What it checks' },
  { id: 'trust', label: 'Security' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'faq', label: 'FAQ' },
];

/* ───────────────────────── Navigation ───────────────────────── */

function SiteNav() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-dashed border-line/80 bg-canvas/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6">
        <Link to="/" aria-label="PR Pilot home">
          <Logo compact />
        </Link>
        <nav aria-label="Sections" className="hidden items-center gap-6 text-sm text-muted lg:flex">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="hover:text-ink">
              {s.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          {me ? (
            <Link to="/app" className={buttonClasses('primary')}>
              Open dashboard
            </Link>
          ) : (
            <>
              <Link to="/signin" className={buttonClasses('ghost')}>
                Sign in
              </Link>
              <Link to="/signup" className={buttonClasses('primary')}>
                Get started free
              </Link>
            </>
          )}
        </div>
        <button
          type="button"
          className="ml-auto rounded-md p-2 text-muted hover:bg-raised sm:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-dashed border-line px-4 pb-4 sm:hidden">
          <nav aria-label="Sections" className="flex flex-col py-2">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} onClick={() => setOpen(false)} className="py-2 text-sm text-muted hover:text-ink">
                {s.label}
              </a>
            ))}
          </nav>
          <div className="flex gap-2">
            {me ? (
              <Link to="/app" className={cn(buttonClasses('primary'), 'flex-1')}>
                Open dashboard
              </Link>
            ) : (
              <>
                <Link to="/signin" className={cn(buttonClasses('secondary'), 'flex-1')}>
                  Sign in
                </Link>
                <Link to="/signup" className={cn(buttonClasses('primary'), 'flex-1')}>
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/* ───────────────────────── Hero ───────────────────────── */

const HERO_LINES: Array<{ n: number; code: ReactNode; flagged?: boolean }> = [
  { n: 7, code: <><span className="text-chalk-pink">router</span>.post(<span className="text-chalk-green">&apos;/orders/:id/refund&apos;</span>, <span className="text-chalk-pink">async</span> (req, res) =&gt; {'{'}</> },
  {
    n: 8,
    flagged: true,
    code: (
      <>
        {'  '}<span className="text-chalk-pink">const</span> q = <span className="text-chalk-green">`SELECT * FROM orders WHERE id = </span>
        <ChalkCircle>
          <span className="text-chalk-green">${'{'}req.params.id{'}'}</span>
        </ChalkCircle>
        <span className="text-chalk-green">`</span>;
      </>
    ),
  },
  { n: 9, code: <>  <span className="text-chalk-pink">const</span> order = <span className="text-chalk-pink">await</span> db.query(q);</> },
  { n: 10, code: <>  <span className="text-chalk-pink">if</span> (!order) <span className="text-chalk-pink">return</span> res.status(<span className="text-chalk-blue">404</span>).end();</> },
  { n: 11, code: <>  res.status(<span className="text-chalk-blue">201</span>).json(<span className="text-chalk-pink">await</span> refund(order));</> },
  { n: 12, code: <>{'}'});</> },
];

/** A rough chalk ellipse drawn around inline content. */
function ChalkCircle({ children }: { children: ReactNode }) {
  const id = useId().replace(/:/g, '');
  return (
    <span className="relative inline-block">
      {children}
      <svg
        className="pointer-events-none absolute -inset-x-3 -inset-y-2 h-[calc(100%+1rem)] w-[calc(100%+1.5rem)] overflow-visible"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        aria-hidden
        focusable="false"
      >
        <defs>
          <filter id={`c-${id}`}>
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="5" />
            <feDisplacementMap in="SourceGraphic" scale="1.6" />
          </filter>
        </defs>
        <path
          className="chalk-draw"
          filter={`url(#c-${id})`}
          d="M8 10 C 30 0, 80 1, 95 12 C 102 22, 88 37, 50 38 C 18 39, 0 30, 4 18 C 6 12, 14 8, 22 7"
          fill="none"
          stroke="rgb(var(--chalk-pink))"
          strokeWidth="2.2"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/** The hero's signature: a real-looking diff with the finding circled and annotated in chalk. */
function AnnotatedDiff() {
  return (
    <figure className="relative" aria-label="Example: PR Pilot flags a SQL injection in a Pull Request diff">
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-dashed border-line px-4 py-2.5 text-xs text-muted">
          <GitPullRequest className="h-3.5 w-3.5 text-chalk-blue" aria-hidden />
          <span className="font-mono">src/routes/refunds.ts</span>
          <span className="ml-auto text-add">+6</span>
        </div>
        <pre className="overflow-x-auto py-3 font-mono text-[0.72rem] leading-7 sm:text-[0.78rem]">
          {HERO_LINES.map((l) => (
            <div key={l.n} className={cn('flex pr-4', l.flagged ? 'bg-chalk-pink/10' : 'bg-add/5')}>
              <span className="w-9 shrink-0 select-none pr-3 text-right text-faint">{l.n}</span>
              <span className="w-4 shrink-0 select-none text-add">+</span>
              <code className="whitespace-pre text-ink">{l.code}</code>
            </div>
          ))}
        </pre>
        <div className="border-t border-dashed border-line px-4 py-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-sev-critical/15 px-2 py-0.5 font-medium text-sev-critical ring-1 ring-inset ring-sev-critical/40">
              Critical
            </span>
            <span className="rounded-full bg-raised px-2 py-0.5 text-muted ring-1 ring-inset ring-line">Security</span>
            <span className="text-muted">Line 8 · verified in diff · confidence 93%</span>
          </div>
          <p className="mt-2 text-sm font-medium">SQL query built by interpolating a URL parameter</p>
        </div>
      </div>

      {/* Handwritten note with an arrow up to the circled code. Decorative: the finding text carries the meaning. */}
      <div className="pointer-events-none mt-3 hidden items-start justify-end gap-2 pr-6 sm:flex" aria-hidden>
        <p className="chalk-title rotate-[-3deg] pt-6 text-[1.75rem] text-chalk-pink">SQL injection! use $1 params</p>
        <svg viewBox="0 0 60 60" className="h-14 w-14 overflow-visible">
          <g fill="none" stroke="rgb(var(--chalk-pink))" strokeWidth="2.6" strokeLinecap="round">
            <path className="chalk-draw chalk-draw-2" d="M8 52 C 30 46, 46 30, 48 6" />
            <path className="chalk-draw chalk-draw-2" d="M38 14 L 48 4 L 55 16" />
          </g>
        </svg>
      </div>
    </figure>
  );
}

function Hero() {
  const { data: info } = usePublicInfo();
  const { data: me } = useMe();
  const free = info?.signupFreeCredits ?? 0;
  return (
    <section className="mx-auto grid max-w-6xl items-center gap-14 px-4 pb-24 pt-14 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:pt-20">
      <div className="min-w-0">
        <p className="inline-flex items-center gap-2 rounded-full border border-dashed border-chalk-yellow/50 px-3 py-1 text-sm text-chalk-yellow">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          AI code review for GitHub Pull Requests
        </p>
        <h1 className="mt-6 text-[2.6rem] font-extrabold leading-[1.05] tracking-tight sm:text-[3.6rem]">
          Catch the bug before it gets merged.
        </h1>
        <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted">
          Paste a Pull Request link. AI Pilot reads the diff and hands you the likely bugs, security holes and missing edge cases —
          each with evidence from the code and a suggested fix you can review.
        </p>
        <ul className="mt-6 space-y-2 text-sm">
          {['Reads only the changed code', 'Every finding checked against the real diff', 'Nothing is posted, pushed or merged without you'].map((t) => (
            <li key={t} className="flex items-center gap-2.5">
              <Check className="h-4 w-4 shrink-0 text-chalk-green" aria-hidden />
              {t}
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to={me ? '/app/new' : '/signup'} className={cn(buttonClasses('primary'), 'h-12 px-6 text-base')}>
            {me ? 'Review a Pull Request' : 'Get started free'}
          </Link>
          {!me && (
            <Link to="/signin" className={cn(buttonClasses('secondary'), 'h-12 px-6 text-base')}>
              Sign in
            </Link>
          )}
        </div>
        {!me && free > 0 && (
          <p className="mt-4 text-sm text-faint">
            {free} free review credits when you sign up. No card needed.
          </p>
        )}
      </div>
      <div className="min-w-0">
        <AnnotatedDiff />
      </div>
    </section>
  );
}

/* ───────────────────────── Shared bits ───────────────────────── */

function SectionHeading({ id, title, color, children }: { id: string; title: string; color: Chalk; children?: ReactNode }) {
  return (
    <div className="max-w-2xl">
      <h2 id={id} className="chalk-title inline-block text-[2.6rem] text-ink sm:text-5xl">
        {title}
        <ChalkUnderline color={color} className="-mt-0.5" />
      </h2>
      {children && <p className="mt-4 text-lg text-muted">{children}</p>}
    </div>
  );
}

/* ───────────────────────── Estimator ───────────────────────── */

function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}) {
  const id = useId();
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <label htmlFor={id} className="text-sm text-muted">
          {label}
        </label>
        <output htmlFor={id} className="text-sm font-semibold tabular-nums">
          {format(value)}
        </output>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-[rgb(var(--chalk-yellow))]"
      />
    </div>
  );
}

function Estimator() {
  const [prs, setPrs] = useState(25);
  const [minutes, setMinutes] = useState(30);
  const [share, setShare] = useState(30);
  const hours = Math.round(((prs * 52) / 12) * minutes * (share / 100) / 60);
  return (
    <section aria-labelledby="estimate" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="card grid gap-10 p-6 sm:p-10 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <h2 id="estimate" className="text-2xl font-bold tracking-tight sm:text-3xl">
            How much review time could you get back?
          </h2>
          <p className="mt-2 text-muted">Move the sliders to match your team. This is a rough estimate from your own numbers, not a promise.</p>
          <div className="mt-8 space-y-6">
            <Slider label="Pull Requests reviewed per week" value={prs} min={1} max={200} onChange={setPrs} format={(v) => `${v}`} />
            <Slider label="Minutes spent hunting for problems per PR" value={minutes} min={5} max={120} step={5} onChange={setMinutes} format={(v) => `${v} min`} />
            <Slider label="Share of that hunting a first AI pass could take on" value={share} min={5} max={60} step={5} onChange={setShare} format={(v) => `${v}%`} />
          </div>
        </div>
        <div className="flex flex-col justify-center rounded-xl border-2 border-dashed border-chalk-yellow/40 p-8 text-center">
          <p className="text-sm text-muted">Estimated reviewer time back</p>
          <p className="chalk-title mt-2 text-7xl text-chalk-yellow" aria-live="polite">
            {hours.toLocaleString()} h
          </p>
          <p className="mt-1 text-sm text-muted">per month</p>
          <p className="mt-6 text-xs text-faint">
            {prs} PRs × {minutes} min × {share}% × 52 weeks ÷ 12 months
          </p>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Before / after ───────────────────────── */

function BeforeAfter() {
  const without = [
    'Reviewers skim large diffs and miss the risky line',
    'Security issues surface after the merge',
    'Feedback arrives hours later, mixed with style nitpicks',
    'Nobody remembers which concerns were checked',
  ];
  const withIt = [
    'A first pass flags likely problems in a couple of minutes',
    'Each finding points at the exact changed line, with evidence',
    'Severity and confidence help you triage what matters',
    'Accept, dismiss or resolve — and keep a history of every review',
  ];
  return (
    <section aria-labelledby="before-after" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <SectionHeading id="before-after" title="Same PR, better review" color="blue">
        PR Pilot doesn&apos;t replace your reviewers. It gives them a head start.
      </SectionHeading>
      <div className="mt-10 grid gap-5 md:grid-cols-2">
        <div className="card border-t-4 border-t-chalk-pink p-6">
          <h3 className="font-semibold">Without PR Pilot</h3>
          <ul className="mt-4 space-y-3">
            {without.map((t) => (
              <li key={t} className="flex gap-3 text-muted">
                <X className="mt-0.5 h-4 w-4 shrink-0 text-chalk-pink" aria-hidden />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="card border-t-4 border-t-chalk-green p-6">
          <h3 className="font-semibold">With PR Pilot</h3>
          <ul className="mt-4 space-y-3">
            {withIt.map((t) => (
              <li key={t} className="flex gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-chalk-green" aria-hidden />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── How it works ───────────────────────── */

const STEPS: Array<{ title: string; body: string; color: Chalk }> = [
  { title: 'Paste a Pull Request link', body: 'Any github.com PR. Public repos work out of the box; private ones need a read-only token on your server.', color: 'yellow' },
  { title: 'AI Pilot reads the diff', body: 'Only the changed hunks are sent. Lock files, generated code and binaries are skipped and listed.', color: 'blue' },
  { title: 'Findings are verified', body: 'File paths and line numbers are checked against the real diff. Duplicates and made-up locations are dropped.', color: 'green' },
  { title: 'You decide what happens', body: 'Accept, dismiss or resolve each finding — and optionally publish the ones you choose as PR comments.', color: 'pink' },
];

function HowItWorks() {
  return (
    <section aria-labelledby="how-it-works" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <SectionHeading id="how-it-works" title="How it works" color="yellow">
        Four steps from link to a reviewed Pull Request.
      </SectionHeading>
      <ol className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <li key={s.title} className="relative">
            <span className={cn('chalk-title block text-6xl', CHALK[s.color].text)} aria-hidden>
              {i + 1}
            </span>
            <h3 className="mt-2 text-lg font-semibold">{s.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ───────────────────────── What it checks ───────────────────────── */

const CHECKS: Array<{ icon: typeof Bug; title: string; body: string; color: Chalk }> = [
  { icon: Bug, title: 'Bugs & logic errors', body: 'Off-by-one mistakes, wrong conditions, broken assumptions.', color: 'pink' },
  { icon: ShieldAlert, title: 'Security', body: 'Injection, missing authorization, unsafe input — only with evidence.', color: 'orange' },
  { icon: Gauge, title: 'Performance', body: 'N+1 queries, unbounded growth, needless work in hot paths.', color: 'yellow' },
  { icon: Wrench, title: 'Error handling', body: 'Swallowed errors, unawaited promises, missing cleanup.', color: 'blue' },
  { icon: ListChecks, title: 'Edge cases', body: 'Nulls, empty lists, boundaries and concurrency.', color: 'green' },
  { icon: TestTube2, title: 'Missing tests', body: 'Important new behaviour with no test coverage in the PR.', color: 'lilac' },
];

function WhatItChecks() {
  return (
    <section aria-labelledby="what-it-checks" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <SectionHeading id="what-it-checks" title="What it looks for" color="pink">
        Actionable problems introduced by the PR — not style nitpicks.
      </SectionHeading>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CHECKS.map(({ icon: Icon, title, body, color }) => (
          <div key={title} className="card p-6">
            <span className={cn('inline-flex rounded-full border-2 border-dashed p-2.5', CHALK[color].border, CHALK[color].text)}>
              <Icon className="h-5 w-5" aria-hidden />
            </span>
            <h3 className="mt-4 font-semibold">{title}</h3>
            <p className="mt-1.5 text-sm text-muted">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Trust ───────────────────────── */

const TRUST: Array<{ icon: typeof Lock; title: string; body: string }> = [
  { icon: KeyRound, title: 'Keys stay on your server', body: 'API keys and tokens never reach the browser, the database or the logs.' },
  { icon: EyeOff, title: 'Only the diff is sent', body: 'No full repository uploads. Repository content is treated as untrusted input.' },
  { icon: GitMerge, title: 'Never merges or pushes', body: 'No code is changed for you. Suggested fixes are proposals you review.' },
  { icon: ShieldCheck, title: 'You approve every comment', body: 'Preview the exact text and confirm before anything is posted to GitHub.' },
  { icon: FileLock2, title: 'Your reviews are yours', body: 'Every review is tied to your account and invisible to other users.' },
  { icon: TimerReset, title: 'Data retention built in', body: 'Reviews expire automatically after the retention period you configure.' },
];

function Trust() {
  return (
    <section aria-labelledby="trust" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <SectionHeading id="trust" title="Built to be trusted" color="green">
        Code review touches your most sensitive asset. PR Pilot is careful with it.
      </SectionHeading>
      <div className="mt-10 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
        {TRUST.map(({ icon: Icon, title, body }) => (
          <div key={title} className="flex gap-4">
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-chalk-green" aria-hidden />
            <div>
              <h3 className="font-semibold">{title}</h3>
              <p className="mt-1 text-sm text-muted">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Pricing ───────────────────────── */

function Pricing() {
  const { data: info } = usePublicInfo();
  const free = info?.signupFreeCredits ?? 0;
  return (
    <section aria-labelledby="pricing" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <SectionHeading id="pricing" title="Simple, per-review pricing" color="orange">
        One credit reviews one Pull Request. Failed reviews are refunded automatically.
      </SectionHeading>
      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        <div className="card relative flex flex-col border-2 border-chalk-yellow/60 p-7">
          <span className="absolute -top-3 left-6 rounded-full bg-cta px-3 py-0.5 text-xs font-semibold text-cta-ink">Start here</span>
          <h3 className="text-lg font-semibold">Free starter</h3>
          <p className="mt-3 flex items-baseline gap-2">
            <span className="chalk-title text-6xl text-chalk-yellow">{free}</span>
            <span className="text-muted">free credits</span>
          </p>
          <ul className="mt-6 flex-1 space-y-2.5 text-sm">
            {['Full AI Pilot review on any GitHub PR', 'Findings, diffs and AI summary', 'Publish selected findings as PR comments'].map((t) => (
              <li key={t} className="flex gap-2.5">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-chalk-green" aria-hidden />
                {t}
              </li>
            ))}
          </ul>
          <Link to="/signup" className={cn(buttonClasses('primary'), 'mt-8')}>
            Create free account
          </Link>
        </div>
        {[
          { title: 'Credit packs', body: 'Top up your balance whenever you need more reviews.', items: ['Credits never expire', 'Same features as the starter', 'Pay only for what you review'] },
          { title: 'Team', body: 'Shared credits and automatic reviews for your repositories.', items: ['Shared credit pool', 'Automatic reviews via GitHub webhooks', 'Priority support'] },
        ].map((p) => (
          <div key={p.title} className="card flex flex-col p-7">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{p.title}</h3>
              <span className="rounded-full bg-chalk-lilac/15 px-2.5 py-0.5 text-xs font-medium text-chalk-lilac">Coming soon</span>
            </div>
            <p className="mt-3 text-sm text-muted">{p.body}</p>
            <ul className="mt-6 flex-1 space-y-2.5 text-sm text-muted">
              {p.items.map((t) => (
                <li key={t} className="flex gap-2.5">
                  <Coins className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden />
                  {t}
                </li>
              ))}
            </ul>
            <span className={cn(buttonClasses('secondary'), 'mt-8 cursor-not-allowed opacity-60')} aria-disabled="true">
              Available soon
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ ───────────────────────── */

const FAQ = [
  ['What exactly gets sent to the AI?', 'The PR title, description and the changed hunks of reviewed files. Lock files, generated or minified code, binaries and oversized diffs are skipped. Your full repository is never uploaded.'],
  ['Are the findings always correct?', 'No. They are potential issues for a human to verify. Each one shows its evidence, a confidence score and whether its line was verified in the diff, so you can judge quickly.'],
  ['Does it work with private repositories?', 'Yes, when the server is configured with a GitHub token that can read those repositories. Public repositories work without any token.'],
  ['What does a credit cost, and what uses one?', 'Starting a review uses one credit. Reading, filtering, dismissing findings and publishing comments are free. If a review fails, the credit is refunded.'],
  ['Will it post to my Pull Request automatically?', 'Never by itself. You choose findings, preview the exact comments and confirm before anything is posted. It never pushes code or merges.'],
  ['What powers AI Pilot?', 'A large language model accessed through its official API. Which model to use is chosen in the server configuration.'],
];

function Faq() {
  return (
    <section aria-labelledby="faq" className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
      <SectionHeading id="faq" title="Questions" color="lilac" />
      <div className="mt-8 divide-y divide-dashed divide-line border-y border-dashed border-line">
        {FAQ.map(([q, a]) => (
          <details key={q} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium marker:hidden">
              {q}
              <span className="text-2xl leading-none text-chalk-yellow transition-transform group-open:rotate-45" aria-hidden>
                +
              </span>
            </summary>
            <p className="mt-3 text-muted">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── Closing + footer ───────────────────────── */

function ClosingCta() {
  const { data: me } = useMe();
  return (
    <section className="mx-auto max-w-6xl px-4 py-24 text-center sm:px-6">
      <h2 className="chalk-title text-5xl text-ink sm:text-7xl">Ready for your next Pull Request.</h2>
      <p className="mx-auto mt-5 max-w-xl text-lg text-muted">Create an account, paste a link, and see what a careful second pair of eyes finds.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to={me ? '/app/new' : '/signup'} className={cn(buttonClasses('primary'), 'h-12 px-6 text-base')}>
          {me ? 'Review a Pull Request' : 'Get started free'}
        </Link>
        <a href="#pricing" className={cn(buttonClasses('secondary'), 'h-12 px-6 text-base')}>
          See pricing
        </a>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-dashed border-line">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 sm:px-6 md:flex-row md:items-start md:justify-between">
        <div className="max-w-xs">
          <Logo compact />
          <p className="mt-3 text-sm text-muted">AI-assisted reviews for GitHub Pull Requests.</p>
        </div>
        <nav aria-label="Footer" className="grid grid-cols-2 gap-x-12 gap-y-2 text-sm">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="text-muted hover:text-ink">
              {s.label}
            </a>
          ))}
          <Link to="/signin" className="text-muted hover:text-ink">
            Sign in
          </Link>
          <Link to="/signup" className="text-muted hover:text-ink">
            Create account
          </Link>
        </nav>
      </div>
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 border-t border-dashed border-line/60 px-4 py-5 text-xs text-faint sm:px-6">
        <span>© {new Date().getFullYear()} PR Pilot</span>
        <span className="inline-flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5 text-chalk-yellow" aria-hidden /> Findings are AI-generated and should be verified before acting on them.
        </span>
      </div>
    </footer>
  );
}

export function HomePage() {
  const { data: info } = usePublicInfo();
  return (
    <div className="min-h-full">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      {info && info.signupFreeCredits > 0 && (
        <div className="bg-chalk-yellow/15 px-4 py-2 text-center text-sm">
          New accounts get <strong className="text-chalk-yellow">{info.signupFreeCredits} free review credits</strong>.{' '}
          <Link to="/signup" className="underline decoration-dashed underline-offset-4 hover:text-chalk-yellow">
            Create yours
          </Link>
        </div>
      )}
      <SiteNav />
      <main id="main">
        <Hero />
        <HowItWorks />
        <Estimator />
        <BeforeAfter />
        <WhatItChecks />
        <Trust />
        <Pricing />
        <Faq />
        <ClosingCta />
      </main>
      <Footer />
    </div>
  );
}
